import { Bell, Check, CheckSquare, MessageCircle, Workflow, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDismissNotification,
} from '../../../hooks/useNotifications';
import type { Notification } from '../../../types/notifications';
import { resolveNotificationLink } from '../../../utils/notificationLinks';
import { useSupportWidgetStore } from '../../../stores/supportWidgetStore';

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const Wrapper = styled.div`
  position: relative;
`;

const BellButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.primary.lightSky};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.status.error.text};
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 360px;
  max-height: 480px;
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const DropdownTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MarkAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.primary.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

const NotificationList = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const NotificationItem = styled.div<{ $unread: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ $unread, theme }) =>
    $unread ? theme.colors.backgrounds.light : 'transparent'};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
  }

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  }
`;

const UnreadDot = styled.div`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary.base};
`;

const ReadDotPlaceholder = styled.div`
  flex-shrink: 0;
  width: 8px;
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  svg {
    flex-shrink: 0;
    width: 13px;
    height: 13px;
    color: ${({ theme }) => theme.colors.text.light};
  }
`;

const NotificationMessage = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NotificationTime = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.light};
  margin-top: 4px;
`;

const DismissButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text.light};
  opacity: 0;
  transition: ${({ theme }) => theme.transitions.fast};

  ${NotificationItem}:hover & {
    opacity: 1;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.status.error.text};
    background: ${({ theme }) => theme.colors.status.error.bg};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[8]};
  color: ${({ theme }) => theme.colors.text.light};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Icon shown at the front of each notification's title, keyed by coarse type. */
function getNotificationIcon(type: Notification['type']) {
  switch (type) {
    case 'TASK':
      return <CheckSquare aria-hidden="true" />;
    case 'WORKFLOW':
      return <Workflow aria-hidden="true" />;
    case 'SUPPORT':
      return <MessageCircle aria-hidden="true" />;
    case 'GENERAL':
    default:
      return null;
  }
}

/**
 * The backend deep-links a requester's own support notifications to
 * `/?support=<id>` (see support-help-center-plan.md Part 2 / support-urls.ts
 * `supportRequesterLinkUrl`) — there's no page at that route, it's a signal
 * to open the widget straight to that thread instead of navigating.
 * Admin/platform queue links (real routes, e.g. `/action-center/support/:id`)
 * fall through to a normal `navigate`. The id itself comes from the
 * structured `metadata.supportRequestId` the backend sets on every SUPPORT
 * notification (support-notify.service.ts) — only the "is this a
 * widget-deep-link" question depends on the linkUrl shape.
 */
function isSupportWidgetDeepLink(notification: Notification): boolean {
  return notification.type === 'SUPPORT' && !!notification.linkUrl?.startsWith('/?support=');
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: unreadCount = 0 } = useUnreadCount();
  const { data: notificationsData, refetch: refetchNotifications } = useNotifications();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const dismissNotification = useDismissNotification();
  const openWidget = useSupportWidgetStore((s) => s.openWidget);
  const openThread = useSupportWidgetStore((s) => s.openThread);

  const notifications: Notification[] = notificationsData?.data ?? [];

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.readAt) {
      markAsRead.mutate(notification.id);
    }
    const link = resolveNotificationLink(notification);
    if (!link) return;

    setIsOpen(false);

    const supportRequestId = notification.metadata?.supportRequestId;
    if (isSupportWidgetDeepLink(notification) && typeof supportRequestId === 'string') {
      openThread(supportRequestId);
      openWidget();
      return;
    }

    navigate(link);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  const handleDismiss = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    dismissNotification.mutate(id);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <BellButton
        aria-label="Notifications"
        onClick={() => {
          // Opening pulls immediately rather than waiting out the poll interval,
          // so the panel matches the badge the user just reacted to.
          if (!isOpen) void refetchNotifications();
          setIsOpen((prev) => !prev);
        }}
      >
        <Bell />
        {unreadCount > 0 && <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>}
      </BellButton>

      <Dropdown $isOpen={isOpen}>
        <DropdownHeader>
          <DropdownTitle>Notifications</DropdownTitle>
          {unreadCount > 0 && (
            <MarkAllButton onClick={handleMarkAllAsRead}>
              <Check size={14} />
              Mark all as read
            </MarkAllButton>
          )}
        </DropdownHeader>

        <NotificationList>
          {notifications.length === 0 ? (
            <EmptyState>
              <Bell size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
              No notifications
            </EmptyState>
          ) : (
            notifications.map((n) => (
              <NotificationItem
                key={n.id}
                $unread={!n.readAt}
                onClick={() => handleNotificationClick(n)}
              >
                {n.readAt ? <ReadDotPlaceholder /> : <UnreadDot />}
                <NotificationContent>
                  <NotificationTitle>
                    {getNotificationIcon(n.type)}
                    <span>{n.title}</span>
                  </NotificationTitle>
                  <NotificationMessage>{n.message}</NotificationMessage>
                  <NotificationTime>{timeAgo(n.createdAt)}</NotificationTime>
                </NotificationContent>
                <DismissButton
                  onClick={(e) => handleDismiss(e, n.id)}
                  aria-label="Dismiss notification"
                >
                  <X size={14} />
                </DismissButton>
              </NotificationItem>
            ))
          )}
        </NotificationList>
      </Dropdown>
    </Wrapper>
  );
};

export default NotificationBell;
